import { useCallback, useEffect, useMemo, useState } from "react";
import { Button as UIButton, Tooltip } from "@mui/material";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import DeleteBin2LineIcon from "remixicon-react/DeleteBin2LineIcon";
import { createUseStyles } from "$utils/createUseStyles";
import { Badge, Modal, Text } from "$components";
import JTable from "$components/JTable/JTable";
import { IProduct } from "$containers";
import NumberInput from "$containers/EDB/NumberInput";
import useJStyles from "$hooks/useJStyles";
import { ProductService } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

const useStyles = createUseStyles(() => ({
  productDesignation: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    "-webkit-box-orient": "vertical",
    "-webkit-line-clamp": 2,
  },
  deleteButton: {
    color: "rgba(0, 0, 0) !important",
    border: "none !important",
    "&:hover": {
      color: "rgba(0, 0, 0) !important",
      backgroundColor: "rgba(0, 0, 0, 0.04) !important",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.26) !important",
    },
    "& svg": {
      color: "inherit !important",
    },
  },
}));

type Props = {
  companyId: string;
  cart: any;
  handleClickProduct: (product: IProduct) => void;
  addToCart: (product: any, value: number) => void;
  removeFromCart: (product: any) => void;
};

function CartCategoryTable({
  products,
  isCatalogue,
  classes,
  handleClickProduct,
  handleProductDelete,
  addToCart,
}: {
  products: any[];
  isCatalogue: boolean;
  classes: any;
  handleClickProduct: (product: IProduct) => void;
  handleProductDelete: (product: any) => void;
  addToCart: (product: any, value: number) => void;
}) {
  const columns = useMemo(
    () => [
      {
        id: "designation",
        Header: isCatalogue ? "Produits catalogue" : "Produits hors catalogue",
        Cell: ({ row }: any) => (
          <Text type="important" className={classes.productDesignation}>
            {row.original.designation}
          </Text>
        ),
        meta: { width: 500 },
      },
      {
        id: "ref",
        Header: "Référence",
        Cell: ({ row }: any) => (
          <Text>
            {(row.original.isReferenced !== false && row.original.reference) ||
              "-"}
          </Text>
        ),
        meta: { width: 250 },
      },
      {
        id: "unit",
        Header: "Unité",
        Cell: ({ row }: any) => row.original.unit,
        meta: { width: 150 },
      },
      {
        id: "value",
        Header: "Quantité",
        Cell: ({ row }: any) => (
          <div onClick={(e) => e.stopPropagation()}>
            <NumberInput
              value={row.original.qty}
              onChange={(val) => addToCart(row.original._id, val)}
            />
          </div>
        ),
        meta: { width: 120 },
      },
      {
        id: "stockStatus",
        Header: "Stock",
        Cell: ({ row }: any) =>
          !!row.original.isReferenced &&
          ((row.original.stock?.quantity || 0) > 0 ? (
            <Badge>
              Dispo (
              {(row.original.stock?.quantity ?? 0) -
                (row.original.stock?.edbReservedQuantity ?? 0)}
              )
            </Badge>
          ) : (
            <Badge color="red">Rupture de stock</Badge>
          )),
      },
      {
        id: "stockWarning",
        Header: "",
        Cell: ({ row }: any) =>
          !!row.original.isReferenced &&
          row.original.qty > row.original.stock?.quantity && (
            <Tooltip
              title="La quantité demandée est supérieure à la quantité disponible"
              placement="top"
            >
              <ReportProblemOutlined style={{ color: "red" }} />
            </Tooltip>
          ),
      },
      {
        id: "button",
        Header: "",
        Cell: ({ row }: any) => (
          <UIButton
            variant="text"
            style={{ textTransform: "none" }}
            onClick={(e) => {
              e.stopPropagation();
              handleProductDelete(row.original);
            }}
            className={classes.deleteButton}
          >
            <DeleteBin2LineIcon />
          </UIButton>
        ),
        meta: { width: 10 },
      },
    ],
    [isCatalogue, classes, addToCart, handleProductDelete]
  );

  return (
    <JTable
      data={products}
      columns={columns}
      setPageIndex={() => {}}
      hideCheckboxes
      hideFilters
      usePaper={false}
      rowsPerPage={products.length || 50}
      rowProps={(row) => ({
        onClick: () => handleClickProduct(row.original),
        style: { cursor: "pointer" },
      })}
    />
  );
}

function TableCart({
  companyId,
  cart,
  handleClickProduct,
  addToCart,
  removeFromCart,
}: Props) {
  const classes = useStyles();
  const { jclasses } = useJStyles();
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [productToDelete, setProductToDelete] = useState<any>(null);
  const [cartProducts, setCartProducts] = useState([]);

  // open and close cart dialog confirmation
  const handleProductDelete = (product: any) => {
    setOpenConfirmDelete(true);
    setProductToDelete(product);
  };

  const handleCancelDialog = () => {
    setOpenConfirmDelete(false);
  };

  const handleValidateDialog = () => {
    setOpenConfirmDelete(false);
    removeFromCart(productToDelete._id);
  };

  const getCartProducts = useCallback(async () => {
    if (!companyId) {
      setCartProducts([]);
      return;
    }

    const notRefProducts = [];
    const refProducts = [];
    await Promise.all(
      cart.map(async (p: any) => {
        if (p.productID) {
          const productPromise = await queryClient.fetchQuery({
            queryKey: [
              CacheInvalidationAction.GET_PRODUCT_STOCK,
              p.productID,
              companyId,
            ],

            queryFn: () =>
              ProductService.getProductStock({
                id: p.productID,
                company: companyId,
              }),

            gcTime: 5 * 60 * 1000,
          });
          const product = productPromise.data[0];
          if (product) {
            product.qty = p.qty;

            if (product.isReferenced) {
              refProducts.push(product);
            } else {
              notRefProducts.push(product);
            }
          } else {
            notRefProducts.push(p);
          }
        }
      })
    );

    refProducts.sort((a, b) =>
      (a.designation || "").localeCompare(b.designation || "")
    );
    notRefProducts.sort((a, b) =>
      (a.designation || "").localeCompare(b.designation || "")
    );

    setCartProducts([refProducts, notRefProducts]);
  }, [cart, companyId]);

  useEffect(() => {
    getCartProducts();
  }, [getCartProducts]);

  return (
    <div>
      {cart && cart.length > 0 && (
        <div>
          {cartProducts.map((filteredCat: any, index: number) => (
            <div key={index}>
              {filteredCat && filteredCat.length > 0 && (
                <CartCategoryTable
                  products={filteredCat}
                  isCatalogue={index === 0}
                  classes={classes}
                  handleClickProduct={handleClickProduct}
                  handleProductDelete={handleProductDelete}
                  addToCart={addToCart}
                />
              )}
            </div>
          ))}
        </div>
      )}
      <Modal
        title="Confirmation de suppression"
        open={openConfirmDelete}
        onCancel={handleCancelDialog}
        onValidate={handleValidateDialog}
      >
        <div className={jclasses.jDialogContent}>
          <Text>Êtes vous sûr de vouloir continuer ?</Text>
        </div>
      </Modal>
    </div>
  );
}

export default TableCart;
